import { DesignSystemFont, DsFile, DsMode } from './interfaces';

// Convert font family name to a safe filename
// "Atak Regular" -> "atak-regular"
export function fontFamilyToFilename(fontFamily: string): string {
	return fontFamily.toLowerCase().replace(/\s+/g, '-');
}

export interface BundledDs {
	theme: string; // concatenated theme/*.css content
	styles: string; // concatenated styles (tokens + recipes + _internal + other styles/*.css)
	componentsCss: string; // concatenated components/*/*.css content
	componentsJs: string; // concatenated components/*/*.js content
}

// Checks if a CSS file is a mode-specific override (e.g., "colors.dark.css" for mode "dark")
// returns the mode id if it's a mode file, null otherwise
function getModeIdForFile(filePath: string, modeIds: string[]): string | null {
	for (const modeId of modeIds) {
		if (filePath.endsWith(`.${modeId}.css`)) {
			return modeId;
		}
	}
	return null;
}

// Adds file to mode map or base array depending on whether it's mode-specific
function addToModeOrBase(
	file: DsFile,
	modeId: string | null,
	modeMap: Map<string, DsFile[]>,
	baseArray: DsFile[]
): void {
	if (modeId) {
		if (!modeMap.has(modeId)) modeMap.set(modeId, []);
		modeMap.get(modeId)!.push(file);
	} else {
		baseArray.push(file);
	}
}

// Replaces :root selectors with [data-mode="<modeId>"] for non-default mode scoping
function scopeCssToMode(css: string, modeId: string): string {
	return css.replace(/:root/g, `[data-mode="${modeId}"]`);
}

// Renders a DsFile as a bundled CSS chunk with optional mode scoping.
// Default mode content is appended as-is (stays in :root).
// Non-default mode content gets :root replaced with [data-mode="<modeId>"].
function renderFileContent(
	file: DsFile,
	modeId: string | null,
	defaultModeId: string | null
): string {
	const header = `/* ${file.path} */\n`;
	if (!modeId || modeId === defaultModeId) {
		return header + file.content;
	}
	return header + scopeCssToMode(file.content, modeId);
}

// Parses config/display.json from a set of ds_files.
// returns { bg, modes } or null if file is missing or unparseable
export function parseDsDisplay(files: DsFile[]): { bg?: string; modes?: DsMode[] } | null {
	const file = files.find((f) => f.path === 'config/display.json');
	if (!file) return null;
	try {
		const raw = JSON.parse(file.content);
		const result: { bg?: string; modes?: DsMode[] } = {};
		if (raw.bg) result.bg = raw.bg;
		if (Array.isArray(raw.modes) && raw.modes.length > 0) {
			result.modes = raw.modes
				.filter((t: any) => typeof t.id === 'string' && typeof t.label === 'string')
				.map((t: any) => ({
					...t,
					// Support legacy "editorPreview.bg" and "preview.bg" keys
					bg: t.bg ?? t.editorPreview?.bg ?? t.preview?.bg,
				}));
		}
		return result.bg || result.modes ? result : null;
	} catch {
		return null;
	}
}

// Bundles ds_files into 4 runtime bundles:
// - theme: All theme/*.css files concatenated (with mode-specific files scoped via [data-mode])
// - styles: styles/tokens.css first, then recipes, then _internal, then other styles/*.css
// - componentsCss: All components/*/*.css files concatenated
// - componentsJs: All components/*/*.js files concatenated
//
// When modes are provided, mode-specific files (e.g., colors.dark.css) are detected and scoped:
// - Base files (no mode suffix): always in :root (as-is)
// - Default mode overrides: appended as-is (still in :root)
// - Non-default mode overrides: :root replaced with [data-mode="<modeId>"]
export function bundleDsFiles(files: DsFile[], modes?: DsMode[]): BundledDs {
	const modeIds = modes?.map((t) => t.id) || [];
	const defaultModeId = modes?.find((t) => t.default)?.id ?? modes?.[0]?.id ?? null;

	// Categorize files
	const themeBaseFiles: DsFile[] = [];
	const modeOverrideFiles: Map<string, DsFile[]> = new Map(); // modeId -> files
	const tokensFile: DsFile[] = [];
	const tokensOverrideFiles: Map<string, DsFile[]> = new Map();
	const recipeFiles: DsFile[] = [];
	const recipeOverrideFiles: Map<string, DsFile[]> = new Map();
	const internalFiles: DsFile[] = [];
	const internalOverrideFiles: Map<string, DsFile[]> = new Map();
	const otherStylesFiles: DsFile[] = [];
	const otherStylesOverrideFiles: Map<string, DsFile[]> = new Map();
	const componentCssFiles: DsFile[] = [];
	const componentJsFiles: DsFile[] = [];

	for (const file of files) {
		if (file.path.startsWith('theme/') && file.path.endsWith('.css')) {
			addToModeOrBase(
				file,
				getModeIdForFile(file.path, modeIds),
				modeOverrideFiles,
				themeBaseFiles
			);
		} else if (file.path === 'styles/tokens.css') {
			tokensFile.push(file);
		} else if (
			file.path.startsWith('styles/') &&
			file.path.endsWith('.css') &&
			file.path.includes('tokens.')
		) {
			addToModeOrBase(
				file,
				getModeIdForFile(file.path, modeIds),
				tokensOverrideFiles,
				otherStylesFiles
			);
		} else if (file.path.startsWith('styles/recipes/') && file.path.endsWith('.css')) {
			addToModeOrBase(file, getModeIdForFile(file.path, modeIds), recipeOverrideFiles, recipeFiles);
		} else if (file.path.startsWith('styles/_internal/') && file.path.endsWith('.css')) {
			addToModeOrBase(
				file,
				getModeIdForFile(file.path, modeIds),
				internalOverrideFiles,
				internalFiles
			);
		} else if (file.path.startsWith('styles/') && file.path.endsWith('.css')) {
			addToModeOrBase(
				file,
				getModeIdForFile(file.path, modeIds),
				otherStylesOverrideFiles,
				otherStylesFiles
			);
		} else if (file.path.startsWith('components/') && file.path.endsWith('.css')) {
			componentCssFiles.push(file);
		} else if (file.path.startsWith('components/') && file.path.endsWith('.js')) {
			componentJsFiles.push(file);
		}
	}

	// Sort base files
	themeBaseFiles.sort((a, b) => a.path.localeCompare(b.path));
	recipeFiles.sort((a, b) => a.path.localeCompare(b.path));
	internalFiles.sort((a, b) => a.path.localeCompare(b.path));
	otherStylesFiles.sort((a, b) => a.path.localeCompare(b.path));
	componentCssFiles.sort((a, b) => a.path.localeCompare(b.path));
	componentJsFiles.sort((a, b) => a.path.localeCompare(b.path));

	// Bundle theme: base files first, then mode overrides (default mode as-is, others scoped)
	const modeParts: string[] = themeBaseFiles.map((f) => `/* ${f.path} */\n${f.content}`);

	// Append default mode overrides first (as-is), then non-default (scoped)
	if (defaultModeId && modeOverrideFiles.has(defaultModeId)) {
		const files = modeOverrideFiles
			.get(defaultModeId)!
			.sort((a, b) => a.path.localeCompare(b.path));
		modeParts.push(...files.map((f) => renderFileContent(f, defaultModeId, defaultModeId)));
	}
	for (const modeId of modeIds) {
		if (modeId === defaultModeId) continue;
		const files = modeOverrideFiles.get(modeId);
		if (files) {
			const sorted = files.sort((a, b) => a.path.localeCompare(b.path));
			modeParts.push(...sorted.map((f) => renderFileContent(f, modeId, defaultModeId)));
		}
	}
	const themeContent = modeParts.join('\n\n');

	// Bundle styles (tokens first, then recipes, then internal, then other styles)
	// Then append theme overrides for each category
	const stylesParts: string[] = [];
	if (tokensFile.length > 0) {
		stylesParts.push(`/* ${tokensFile[0].path} */\n${tokensFile[0].content}`);
	}
	if (recipeFiles.length > 0) {
		stylesParts.push(...recipeFiles.map((f) => `/* ${f.path} */\n${f.content}`));
	}
	if (internalFiles.length > 0) {
		stylesParts.push(...internalFiles.map((f) => `/* ${f.path} */\n${f.content}`));
	}
	if (otherStylesFiles.length > 0) {
		stylesParts.push(...otherStylesFiles.map((f) => `/* ${f.path} */\n${f.content}`));
	}

	// Append mode overrides for styles (default first, then non-default)
	const allStyleOverrides = [
		tokensOverrideFiles,
		recipeOverrideFiles,
		internalOverrideFiles,
		otherStylesOverrideFiles,
	];
	if (defaultModeId) {
		for (const overrideMap of allStyleOverrides) {
			const files = overrideMap.get(defaultModeId);
			if (files) {
				const sorted = files.sort((a, b) => a.path.localeCompare(b.path));
				stylesParts.push(...sorted.map((f) => renderFileContent(f, defaultModeId, defaultModeId)));
			}
		}
	}
	for (const modeId of modeIds) {
		if (modeId === defaultModeId) continue;
		for (const overrideMap of allStyleOverrides) {
			const files = overrideMap.get(modeId);
			if (files) {
				const sorted = files.sort((a, b) => a.path.localeCompare(b.path));
				stylesParts.push(...sorted.map((f) => renderFileContent(f, modeId, defaultModeId)));
			}
		}
	}
	const stylesContent = stylesParts.join('\n\n');

	// Bundle components.css
	const componentsCssContent = componentCssFiles
		.map((f) => `/* ${f.path} */\n${f.content}`)
		.join('\n\n');

	// Bundle components.js — each file wrapped in an IIFE so top-level const/let
	// declarations (e.g. SHADOW_CSS) don't collide across components
	// This is absolutely needed because we often have SHADOW_CSS declared.
	// However this is also important to prevent accidental collisions (e.g. both components declare an onClick, or constant that shares a name)
	const componentsJsContent = componentJsFiles
		.map((f) => `/* ${f.path} */\n;(function() {\n${f.content}\n})();`)
		.join('\n\n');

	return {
		theme: themeContent,
		styles: stylesContent,
		componentsCss: componentsCssContent,
		componentsJs: componentsJsContent,
	};
}

// Build Google Fonts API v2 URL from font configuration
// Format: family=Inter:ital,wght@0,400;0,700;1,400;1,700 (when italic included)
// Format: family=Inter:wght@400;700 (weights only)
export function buildGoogleFontsCdnUrl(fonts: DesignSystemFont[]): string | null {
	const googleFonts = fonts.filter((f) => f.source === 'google' && f.fontFamily);
	if (googleFonts.length === 0) return null;

	const families = googleFonts.map((f) => {
		const family = f.fontFamily.replace(/ /g, '+');
		const weights = f.fontWeight?.length ? f.fontWeight : ['400'];
		const styles = f.fontStyle || ['normal'];
		const hasItalic = styles.includes('italic');

		if (hasItalic) {
			// Use ital,wght format: ital,wght@0,400;0,700;1,400;1,700
			const specs: string[] = [];
			if (styles.includes('normal')) {
				weights.forEach((w) => specs.push(`0,${w}`));
			}
			if (styles.includes('italic')) {
				weights.forEach((w) => specs.push(`1,${w}`));
			}
			return `family=${family}:ital,wght@${specs.join(';')}`;
		} else {
			// Simple wght format: wght@400;700
			return `family=${family}:wght@${weights.join(';')}`;
		}
	});

	return `https://fonts.googleapis.com/css2?${families.join('&')}&display=swap`;
}

// Build @font-face CSS rules for custom fonts
// Returns CSS string with @font-face declarations
// - If attachmentBaseUrl is provided: uses absolute URLs (for studio previews)
// - If attachmentBaseUrl is omitted: uses relative paths (for LLM-generated HTML via SiteMap)
export function buildCustomFontsCss(
	fonts: DesignSystemFont[],
	attachmentBaseUrl?: string
): string | null {
	const customFonts = fonts.filter((f) => f.source === 'custom' && f.files);
	if (customFonts.length === 0) return null;

	return customFonts
		.map((f) => {
			const sources: string[] = [];
			const safeName = fontFamilyToFilename(f.fontFamily);
			if (f.files?.woff2) {
				const url = attachmentBaseUrl
					? `${attachmentBaseUrl}/${f.files.woff2}?proxy=true`
					: `${safeName}.woff2`;
				sources.push(`url('${url}') format('woff2')`);
			}
			if (f.files?.woff) {
				const url = attachmentBaseUrl
					? `${attachmentBaseUrl}/${f.files.woff}?proxy=true`
					: `${safeName}.woff`;
				sources.push(`url('${url}') format('woff')`);
			}

			if (sources.length === 0) return null;

			return `@font-face {
  font-family: '${f.fontFamily}';
  src: ${sources.join(',\n       ')};
  font-weight: 400;
  font-style: normal;
  font-display: swap;
}`;
		})
		.filter((css) => css !== null)
		.join('\n\n');
}

// Get custom font file mappings for SiteMap
// Returns { 'atak-regular.woff2': 'cas-hash.woff2', ... }
export function getCustomFontSiteMapEntries(fonts: DesignSystemFont[]): Record<string, string> {
	const entries: Record<string, string> = {};
	const customFonts = fonts.filter((f) => f.source === 'custom' && f.files);

	for (const font of customFonts) {
		const safeName = fontFamilyToFilename(font.fontFamily);
		if (font.files?.woff2) {
			entries[`${safeName}.woff2`] = font.files.woff2;
		}
		if (font.files?.woff) {
			entries[`${safeName}.woff`] = font.files.woff;
		}
	}

	return entries;
}
